"use client";

import { useParams } from "react-router-dom";
import { useLanguageNavigate } from "@/lib/hooks/useLanguageNavigate";
import { LanguageLink } from "@/components/LanguageLink";
import { useState, useEffect, useMemo } from "react";
import { cn } from "@/lib/utils";
import { useVideoJob, useVideoGroup, useBackend, useApiError, useCancelVideo, useDeleteVideo } from "@/lib/hooks";
import { VideoStatusBadge, SocialStatusBadge } from "./components";
import { DashboardHeader } from "@/pages/dashboard/components";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "@/components/ui/use-toast";
import { CancelDeleteDialog } from "@/components/CancelDeleteDialog";
import { ArrowLeft, Download, RefreshCw, ImageOff, Mail, Instagram, ExternalLink, Facebook, Music, Youtube, Trash2, Play } from "lucide-react";
import { useTranslation } from "react-i18next";
import { trackVideoDownload, trackVideoCreation } from "@/lib/analytics";
import { useInstagramPosts } from "@/lib/hooks/useInstagram";
import { useTikTokPosts } from "@/lib/hooks/useTikTok";
import { useYouTubePosts } from "@/lib/hooks/useYouTube";
import { useQueryClient } from "@tanstack/react-query";
import { InstagramPublishDialog } from "@/components/InstagramPublishDialog";
import { TikTokPublishDialog } from "@/components/TikTokPublishDialog";
import { YouTubePublishDialog } from "@/components/YouTubePublishDialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Root as VisuallyHidden } from "@radix-ui/react-visually-hidden";

function VideoDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useLanguageNavigate();
  const backend = useBackend();
  const { handleError } = useApiError();

  const { data: job, isLoading, error } = useVideoJob(id);
  
  // Fetch group data if groupId is present
  const { data: groupData } = useVideoGroup(job?.groupId);
  const groupJobs = useMemo(() => groupData?.jobs || [], [groupData]);

  const [isRetrying, setIsRetrying] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [posterError, setPosterError] = useState(false);
  const [showPublishDialog, setShowPublishDialog] = useState(false);
  const [showTikTokDialog, setShowTikTokDialog] = useState(false);
  const [showYouTubeDialog, setShowYouTubeDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedMedia, setSelectedMedia] = useState<{ url: string; type: string } | null>(null);
  const { t } = useTranslation();

  const cancelVideo = useCancelVideo();
  const deleteVideo = useDeleteVideo();

  const { data: igPostsData } = useInstagramPosts(id);
  const { data: tkPostsData } = useTikTokPosts(id);
  const { data: ytPostsData } = useYouTubePosts(id);
  const queryClient = useQueryClient();


  const isComplete = () => {
    return job?.status === "completed" && job.videoUrl;
  }

  const isProcessing = () => {
    return job?.status === "processing" || job?.status === "pending";
  }

  useEffect(() => {
    if (job?.imageUrls?.[0]) {
      const img = new Image();
      img.src = job.imageUrls[0];
      img.onerror = () => setPosterError(true);
      img.onload = () => setPosterError(false);
    }
  }, [job?.imageUrls]);

  const handleDownload = async () => {
    if (!id) return;

    try {
      const { downloadUrl, filename } = await backend.video.download(id);

      // Track video download
      trackVideoDownload(id);

      // Create a temporary link and trigger download
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      handleError(error, t("videoDetail.downloadError"));
    }
  };

  const handleRetry = async () => {
    if (!id) return;

    setIsRetrying(true);
    try {
      trackVideoCreation.retry(id);

      await backend.video.retry(id);

      queryClient.invalidateQueries({ queryKey: ["video", id] });

      toast({
        title: t("videoDetail.retryToast"),
        description: t("videoDetail.retryToastDesc"),
      });
    } catch (error) {
      handleError(error, t("videoDetail.retryError"));
    } finally {
      setIsRetrying(false);
    }
  };

  const handleDeleteOrCancel = async () => {
    if (!id) return;

    setIsDeleting(true);
    try {
      const isPendingOrProcessing = job?.status === "pending" || job?.status === "processing";
      
      if (isPendingOrProcessing) {
        await cancelVideo.mutateAsync(id);
        toast({
          title: t("videoDetail.cancelSuccess"),
          description: t("videoDetail.cancelSuccessDesc"),
        });
      } else {
        await deleteVideo.mutateAsync(id);
        toast({
          title: t("videoDetail.deleteSuccess"),
          description: t("videoDetail.deleteSuccessDesc"),
        });
      }
      
      setShowDeleteDialog(false);
      navigate("/videos");
    } catch (error) {
      handleError(error, t("videoDetail.deleteError"));
    } finally {
      setIsDeleting(false);
    }
  };

  const isPendingOrProcessing = job?.status === "pending" || job?.status === "processing";

  if (isLoading) {
    return (
      <div className="min-h-screen bg-lavender flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-8 h-8 border-2 border-rose-dark border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-muted-foreground">{t("videoDetail.loadingVideo")}</p>
        </div>
      </div>
    );
  }

  if (error || !job) {
    return (
      <div className="min-h-screen bg-lavender flex items-center justify-center">
        <div className="text-center">
          <p className="text-destructive mb-4">{t("videoDetail.videoNotFound")}</p>
          <Button variant="outline" onClick={() => navigate("/videos")}>
            {t("videoDetail.backToList")}
          </Button>
        </div>
      </div>
    );
  }

  const createdAt = new Date(job.createdAt).toLocaleDateString("sv-SE", {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

  return (
    <div className="min-h-screen bg-lavender">
      <main className="max-w-7xl 2k:max-w-[80%] 4k:max-w-[80%] mx-auto px-6 py-8">
        <DashboardHeader />

        {/* Back button */}
        <LanguageLink
          to="/videos"
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-6 2k:text-lg 2k:gap-3 4k:text-4xl 4k:gap-6 2k:mb-10 4k:mb-20"
        >
          <ArrowLeft className="size-4 2k:w-5 2k:h-5 4k:size-10" />
          {t("videoDetail.backToMyVideos")}
        </LanguageLink>

        {/* Header */}
        <div className="flex items-start justify-between mb-6 2k:mb-10 4k:mb-14">
          <div className="flex-1">
            <div className="flex items-center gap-3 2k:gap-6 4k:gap-10">
              <h1 className="font-h2 2k:text-4xl 4k:text-7xl">{job.title}</h1>
              <VideoStatusBadge status={job.status} />
            </div>
            {job.description && (
              <p className="text-muted-foreground mt-1 2k:text-xl 4k:text-3xl">{job.description}</p>
            )}
            <div className="flex items-center gap-4 mt-2 2k:mt-4 4k:mt-8">
              <p className="text-sm text-muted-foreground 2k:text-lg 4k:text-3xl">
                {t("common.createdAt", { date: createdAt })}
              </p>
            </div>
          </div>
          {/* Cancel/Delete button inline */}
          {(isPendingOrProcessing || job.status === "completed" || job.status === "failed") && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowDeleteDialog(true)}
              className="text-muted-foreground hover:text-destructive 2k:text-lg"
            >
              <Trash2 className="size-3.5 2k:size-4" />
              <span className="hidden sm:inline ml-2">
                {isPendingOrProcessing ? t("videoDetail.cancel") : t("videoDetail.delete")}
              </span>
            </Button>
          )}
        </div>

        {/* Aspect Ratio Tabs */}
        {groupJobs.length > 1 && (
          <div className="flex gap-2 mb-6 p-1 bg-white/50 backdrop-blur-sm rounded-lg border border-rose-dark/10 w-fit 2k:p-2 2k:gap-3 2k:mb-10 4k:p-3 4k:gap-5 4k:mb-14">
            {groupJobs.map((groupJob) => {
              const ratio = groupJob.settings?.aspectRatio || "16:9";
              const isActive = groupJob.id === id;
              
              return (
                <button
                  key={groupJob.id}
                  onClick={() => navigate(`/videos/${groupJob.id}`)}
                  className={cn(
                    "px-4 py-2 rounded-md text-sm font-medium transition-all duration-200",
                    "2k:px-7 2k:py-3.5 2k:text-lg 4k:py-7 4k:text-3xl",
                    isActive 
                      ? "bg-rose-dark text-white shadow-sm" 
                      : "text-muted-foreground hover:text-rose-dark hover:bg-rose-dark/5"
                  )}
                >
                  {ratio === "9:16" ? t("createVideo.vertical") || "9:16" : t("createVideo.horizontal") || "16:9"}
                </button>
              );
            })}
          </div>
        )}

        {/* Video player / Status */}
        <Card className="bg-white rounded-md border border-rose-dark shadow-[0_4px_13px_rgba(0,0,0,0.1)] mb-6 2k:mb-12 4k:mb-16 p-0 2k:rounded-lg 4k:rounded-xl">
          <CardContent className="p-2 md:p-6 2k:p-10 4k:p-16">
            {isComplete() ? (
              <div className="aspect-video bg-black rounded-md flex items-center justify-center overflow-hidden">
                <video
                  src={job.videoUrl}
                  controls
                  className="w-full h-full object-contain"
                  poster={!posterError ? job.imageUrls?.[0] : undefined}
                  onEncrypted={() => {}} // dummy
                />
              </div>
            ) : isProcessing() ? (
              <div className="aspect-video bg-muted rounded-md flex items-center justify-center 2k:rounded-lg 4k:rounded-xl">
                <div className="text-center scale-[0.8] md:scale-100">
                  <div className="size-12 2k:size-24 4k:size-36 border-3 4k:border-4 border-rose-dark border-t-transparent rounded-full animate-spin mx-auto mb-4 2k:mb-8 4k:mb-12" />
                  <p className="font-medium 2k:text-2xl 4k:text-4xl 2k:mb-4 4k:mb-8">
                    {job.status === "pending"
                      ? t("videoDetail.pending")
                      : t("videoDetail.processing")}
                  </p>
                  <div className="flex flex-col items-center gap-2 2k:gap-4 4k:gap-6 mt-2">
                    <p className="text-sm text-muted-foreground 2k:text-xl 4k:text-2xl">
                      {t("videoDetail.processingNote")}
                    </p>
                    <div className="flex items-center gap-2 2k:gap-4 4k:gap-6 px-3 py-1.5 2k:px-6 2k:py-3.5 4k:px-10 4k:py-6 bg-rose-dark/5 rounded-full border border-rose-dark/10 mt-2 2k:mt-6 4k:mt-10">
                      <Mail className="w-3.5 h-3.5 2k:size-6 4k:w-9 4k:h-9 text-rose-dark" />
                      <p className="text-sm font-medium 2k:text-xl 4k:text-2xl text-rose-dark/80">
                        {t("videoDetail.emailNotification")}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="aspect-video bg-muted rounded-md flex items-center justify-center 2k:rounded-lg 4k:rounded-xl">
                <div className="text-center">
                  <p className="font-medium text-destructive 2k:text-2xl 4k:text-4xl">
                    {t("videoDetail.failed")}
                  </p>
                  {job.errorMessage && (
                    <p className="text-sm text-muted-foreground mt-1 2k:text-lg 4k:text-2xl 2k:mt-3 4k:mt-5">
                      {job.errorMessage}
                    </p>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Actions */}
        <div className="flex flex-wrap gap-3 2k:gap-6 4k:gap-8">
          {job.status === "completed" && (
            <>
              <Button onClick={handleDownload} className="2k:rounded-lg 4k:rounded-xl gap-2 2k:gap-4 2k:h-14 2k:px-8! 2k:text-xl 4k:gap-6 4k:h-24 4k:text-3xl">
                <Download className="size-4 2k:size-6 4k:size-10" />
                {t("videoDetail.download")}
              </Button>
              <Button
                onClick={() => setShowPublishDialog(true)}
                className="2k:rounded-lg 4k:rounded-xl gap-2 2k:gap-4 2k:h-14 2k:px-8! 2k:text-xl 4k:gap-6 4k:h-24 4k:text-3xl bg-linear-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white"
              >
                <Instagram className="size-4 2k:size-6 4k:size-10" />
                {t("instagram.publishToInstagram")}
              </Button>
              <Button
                onClick={() => setShowTikTokDialog(true)}
                className="2k:rounded-lg 4k:rounded-xl gap-2 2k:gap-4 2k:h-14 2k:px-8! 2k:text-xl 4k:gap-6 4k:h-24 4k:text-3xl bg-black hover:bg-black/90 text-white"
              >
                <Music className="size-4 2k:size-6 4k:size-10" />
                {t("tiktok.publishToTikTok")}
              </Button>
              <Button
                onClick={() => setShowYouTubeDialog(true)}
                className="2k:rounded-lg 4k:rounded-xl gap-2 2k:gap-4 2k:h-14 2k:px-8! 2k:text-xl 4k:gap-6 4k:h-24 4k:text-3xl bg-red-600 hover:bg-red-700 text-white"
              >
                <Youtube className="size-4 2k:size-6 4k:size-10" />
                {t("youtube.publishToYouTube")}
              </Button>
            </>
          )}

          {job.status === "failed" && (
            <Button
              onClick={handleRetry}
              disabled={isRetrying}
              className="gap-2 2k:gap-4 2k:h-14 2k:px-8! 2k:text-xl 4k:gap-6 4k:h-24 4k:text-3xl"
            >
              <RefreshCw className={`size-4 2k:size-6 4k:w-9 4k:h-9 ${isRetrying ? "animate-spin" : ""}`} />
              {isRetrying ? t("videoDetail.retrying") : t("videoDetail.retry")}
            </Button>
          )}
        </div>

        {/* Instagram post status */}
        {igPostsData?.posts && igPostsData.posts.length > 0 && (
          <div className="mt-4">
            <h3 className="text-sm font-bold text-muted-foreground uppercase tracking-wider mb-3 2k:text-lg 2k:mb-6 4k:text-2xl 4k:mb-10">
              {t("instagram.postHistory")}
            </h3>
            <div className="space-y-2">
              {igPostsData.posts.map((post) => (
                <div key={post.id} className="space-y-0">
                  <div
                    className={cn(
                      "flex items-center justify-between p-3 2k:p-6 4k:p-10 bg-white border border-black/5",
                      post.mirrorToFacebook && post.fbStatus ? "rounded-t-lg 2k:rounded-t-xl 4k:rounded-t-2xl" : "rounded-lg 2k:rounded-xl 4k:rounded-2xl"
                    )}
                  >
                   <div className="flex items-center gap-3 2k:gap-5 4k:gap-8">
                    <Instagram className="size-4 2k:size-6 4k:w-10 4k:h-10 text-purple-500" />
                    <div>
                      <span className="text-sm font-medium 2k:text-xl 4k:text-3xl">
                        {post.postType === "reel" ? t("instagram.reel") : t("instagram.feedVideo")}
                      </span>
                      {post.caption && (
                        <p className="text-xs text-muted-foreground truncate max-w-[200px] 2k:max-w-[400px] 4k:max-w-[600px] 2k:text-base 4k:text-xl">
                          {post.caption}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 2k:gap-4 4k:gap-6">
                    <SocialStatusBadge status={post.status} platform="instagram" />
                    {post.instagramPermalink && (
                      <a
                        href={post.instagramPermalink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-muted-foreground hover:text-foreground"
                      >
                        <ExternalLink className="w-3.5 h-3.5 2k:w-5 2k:h-5 4k:w-8 4k:h-8" />
                      </a>
                    )}
                  </div>
                </div>
                {post.mirrorToFacebook && post.fbStatus && (
                  <div className="flex items-center justify-between p-3 2k:p-6 4k:p-10 pt-0 2k:pt-0 4k:pt-0 -mt-1 bg-white rounded-b-lg 2k:rounded-b-xl 4k:rounded-b-2xl border border-t-0 border-black/5">
                    <div className="flex items-center gap-3 2k:gap-5 4k:gap-8">
                      <Facebook className="size-4 2k:size-6 4k:w-10 4k:h-10 text-blue-600" />
                      <span className="text-sm font-medium 2k:text-xl 4k:text-3xl">
                        {post.fbPostType === "reel"
                          ? t("instagram.facebook.fbReel")
                          : t("instagram.facebook.fbPageVideo")}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 2k:gap-4 4k:gap-6">
                      <SocialStatusBadge status={post.fbStatus} platform="facebook" />
                      {post.fbPermalink && (
                        <a
                          href={post.fbPermalink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-muted-foreground hover:text-foreground"
                        >
                          <ExternalLink className="w-3.5 h-3.5 2k:w-5 2k:h-5 4k:w-8 4k:h-8" />
                        </a>
                      )}
                    </div>
                  </div>
                )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Instagram publish dialog */}
        {id && job && (
          <InstagramPublishDialog
            open={showPublishDialog}
            onOpenChange={setShowPublishDialog}
            jobId={id}
            videoTitle={job.title}
            onPublished={() => queryClient.invalidateQueries({ queryKey: ["instagram-posts", id] })}
          />
        )}

        {/* TikTok post status */}
        {tkPostsData?.posts && tkPostsData.posts.length > 0 && (
          <div className="mt-4">
            <h3 className="text-sm font-bold text-muted-foreground uppercase tracking-wider mb-3 2k:text-lg 2k:mb-6 4k:text-2xl 4k:mb-10">
              {t("tiktok.postHistory")}
            </h3>
            <div className="space-y-2 2k:space-y-4 4k:space-y-6">
              {tkPostsData.posts.map((post) => (
                <div
                  key={post.id}
                  className="flex items-center justify-between p-3 2k:p-6 4k:p-10 bg-white rounded-lg 2k:rounded-xl 4k:rounded-2xl border border-black/5"
                >
                  <div className="flex items-center gap-3 2k:gap-5 4k:gap-8">
                    <Music className="size-4 2k:size-6 4k:w-10 4k:h-10 text-black" />
                    <div>
                      <span className="text-sm font-medium 2k:text-xl 4k:text-3xl">TikTok</span>
                      {post.caption && (
                        <p className="text-xs text-muted-foreground truncate max-w-[200px] 2k:max-w-[400px] 4k:max-w-[600px] 2k:text-base 4k:text-xl">
                          {post.caption}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 2k:gap-4 4k:gap-6">
                    <SocialStatusBadge status={post.status} platform="tiktok" />
                    {post.tiktokShareUrl && (
                      <a
                        href={post.tiktokShareUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-muted-foreground hover:text-foreground"
                      >
                        <ExternalLink className="w-3.5 h-3.5 2k:w-5 2k:h-5 4k:w-8 4k:h-8" />
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* TikTok publish dialog */}
        {id && job && (
          <TikTokPublishDialog
            open={showTikTokDialog}
            onOpenChange={setShowTikTokDialog}
            jobId={id}
            videoTitle={job.title}
            onPublished={() => queryClient.invalidateQueries({ queryKey: ["tiktok-posts", id] })}
          />
        )}

        {/* YouTube post status */}
        {ytPostsData?.posts && ytPostsData.posts.length > 0 && (
          <div className="mt-4">
            <h3 className="text-sm font-bold text-muted-foreground uppercase tracking-wider mb-3 2k:text-lg 2k:mb-6 4k:text-2xl 4k:mb-10">
              {t("youtube.postHistory")}
            </h3>
            <div className="space-y-2 2k:space-y-4 4k:space-y-6">
              {ytPostsData.posts.map((post) => (
                <div
                  key={post.id}
                  className="flex items-center justify-between p-3 2k:p-6 4k:p-10 bg-white rounded-lg 2k:rounded-xl 4k:rounded-2xl border border-black/5"
                >
                  <div className="flex items-center gap-3 2k:gap-5 4k:gap-8">
                    <Youtube className="size-4 2k:size-6 4k:w-10 4k:h-10 text-red-600" />
                    <div>
                      <span className="text-sm font-medium 2k:text-xl 4k:text-3xl">YouTube</span>
                      {post.title && (
                        <p className="text-xs text-muted-foreground truncate max-w-[200px] 2k:max-w-[400px] 4k:max-w-[600px] 2k:text-base 4k:text-xl">
                          {post.title}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 2k:gap-4 4k:gap-6">
                    <SocialStatusBadge status={post.status} platform="youtube" />
                    {post.youtubeUrl && (
                      <a
                        href={post.youtubeUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-muted-foreground hover:text-foreground"
                      >
                        <ExternalLink className="w-3.5 h-3.5 2k:w-5 2k:h-5 4k:w-8 4k:h-8" />
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* YouTube publish dialog */}
        {id && job && (
          <YouTubePublishDialog
            open={showYouTubeDialog}
            onOpenChange={setShowYouTubeDialog}
            jobId={id}
            videoTitle={job.title}
            onPublished={() => queryClient.invalidateQueries({ queryKey: ["youtube-posts", id] })}
          />
        )}

        {/* Source media */}
        {job.imageUrls && job.imageUrls.length > 0 && (
          <div className="mt-8 2k:mt-16 4k:mt-24">
            <h2 className="font-h3 mb-4 2k:text-3xl 4k:text-5xl 2k:mb-8 4k:mb-12">
              {t("videoDetail.sourceMedia", { count: job.imageUrls.length })}
            </h2>
            <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-6 2k:grid-cols-8 4k:grid-cols-6 gap-3 2k:gap-6 4k:gap-14">
              {job.imageUrls.map((url, index) => (
                <SourceMedia 
                  key={index} 
                  url={url} 
                  index={index} 
                  mediaType={job.mediaTypes?.[index] || "image"} 
                  onClick={() => setSelectedMedia({ url, type: job.mediaTypes?.[index] || "image" })}
                />
              ))}
            </div>
          </div>
        )}

        {/* Media Preview Dialog */}
        <Dialog open={!!selectedMedia} onOpenChange={(open) => !open && setSelectedMedia(null)}>
          <DialogContent className="max-w-5xl p-0 overflow-hidden bg-black border-none sm:rounded-lg">
            <DialogHeader>
              <VisuallyHidden>
                <DialogTitle>Media Preview</DialogTitle>
              </VisuallyHidden>
            </DialogHeader>
            <div className="relative w-full h-full flex items-center justify-center min-h-[50vh] max-h-[85vh]">
              {selectedMedia?.type === "video" ? (
                <video
                  src={selectedMedia.url}
                  className="w-full h-full max-h-[85vh] object-contain"
                  controls
                  autoPlay
                />
              ) : (
                <img
                  src={selectedMedia?.url}
                  alt="Preview"
                  className="w-full h-full max-h-[85vh] object-contain"
                />
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* Cancel/Delete Dialog */}
        <CancelDeleteDialog
          open={showDeleteDialog}
          onOpenChange={setShowDeleteDialog}
          variant={isPendingOrProcessing ? "cancel" : "delete"}
          isLoading={isDeleting}
          onConfirm={handleDeleteOrCancel}
        />
      </main>
    </div>
  );
}

function SourceMedia({ url, index, mediaType, onClick }: { url: string; index: number; mediaType: string; onClick: () => void }) {
  const [hasError, setHasError] = useState(false);
  const { t } = useTranslation();

  return (
    <div 
      className="aspect-video rounded-sm overflow-hidden bg-white border border-rose-dark/20 2k:rounded-md 4k:rounded-lg cursor-pointer hover:ring-2 hover:ring-rose-dark transition-all"
      onClick={onClick}
    >
      {!hasError ? (
        mediaType === "video" ? (
          <div className="w-full h-full relative group">
            <video
              src={url}
              className="w-full h-full object-cover"
              muted
              loop
              onMouseOver={(e) => e.currentTarget.play().catch(() => {})}
              onMouseOut={(e) => (e.currentTarget.pause(), e.currentTarget.currentTime = 0)}
            />
            <div className="absolute inset-0 bg-black/20 group-hover:bg-transparent transition-colors flex items-center justify-center">
              <Play className="w-6 h-6 text-white opacity-80 fill-white" />
            </div>
          </div>
        ) : (
          <img
            src={url}
            alt={t("videoDetail.mediaAlt", { index: index + 1 })}
            className="w-full h-full object-cover"
            onError={() => setHasError(true)}
          />
        )
      ) : (
        <div className="w-full h-full flex items-center justify-center text-muted-foreground bg-muted/30">
          <ImageOff className="w-6 h-6 2k:w-10 2k:h-10 4k:w-16 4k:h-16 opacity-40" />
        </div>
      )}
    </div>
  );
}

export { VideoDetailPage };
